import { Card, Row, Col, Tag, Typography, Space, Statistic, Divider, Button } from 'antd';
import { Link } from 'react-router-dom';
import { EyeOutlined, CalendarOutlined, UserOutlined, FireOutlined, TagOutlined } from '@ant-design/icons';
import './index.css';

const { Title, Text, Paragraph } = Typography;

const Home = ({ posts, loggedInUser }) => {
  // 按时间倒序排列文章
  const sortedPosts = [...posts].sort((a, b) => new Date(b.createTime) - new Date(a.createTime));
  // 提取所有标签（去重）
  const allTags = Array.from(new Set(posts.flatMap(post => post.tags)));
  // 热门文章（取前2篇）
  const hotPosts = sortedPosts.slice(0, 2);

  return (
    <div className="home-page">
      {/* 顶部Banner区域 */}
      <div className="home-banner">
        <div className="banner-content">
          <Title level={2} className="banner-title">欢迎来到我的个人博客</Title>
          <Text className="banner-desc">分享技术、生活与成长的点滴</Text>
          {loggedInUser && (
            <Button 
              type="primary" 
              size="large" 
              className="banner-btn"
              onClick={() => window.location.href = '/write'}
            >
              开始撰写文章
            </Button>
          )}
        </div>
      </div>

      {/* 数据统计+标签区域 */}
      <div className="home-stats">
        <Row gutter={[24, 24]}>
          {/* 文章总数 */}
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="stat-card">
              <Statistic
                title="文章总数"
                value={posts.length}
                prefix={<EyeOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          {/* 标签总数 */}
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="stat-card">
              <Statistic
                title="标签总数"
                value={allTags.length}
                prefix={<TagOutlined />}
                valueStyle={{ color: '#27ae60' }}
              />
            </Card>
          </Col>
          {/* 最新文章日期 */}
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="stat-card">
              <Statistic
                title="最新文章"
                value={posts.length > 0 ? sortedPosts[0].createTime : '暂无'}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#f39c12' }}
              />
            </Card>
          </Col>
          {/* 作者信息 */}
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="stat-card">
              <Statistic
                title="博主"
                value={loggedInUser?.username || '匿名'}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#e74c3c' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Divider className="home-divider" />

      {/* 主内容区：文章列表+热门推荐 */}
      <Row gutter={[24, 24]}>
        {/* 左侧：文章列表 */}
        <Col xs={24} lg={16}>
          <div className="section-header">
            <Title level={3}>最新文章</Title>
            <Link to="/archive" className="view-all-link">查看全部</Link>
          </div>

          {sortedPosts.length > 0 ? (
            <Row gutter={[24, 24]}>
              {sortedPosts.map(post => (
                <Col xs={24} sm={12} key={post.id}>
                  <Card hoverable className="post-card">
                    {/* 文章元信息 */}
                    <Space className="post-meta" size="small">
                      <Space>
                        <CalendarOutlined size={12} />
                        <Text size="small">{post.createTime}</Text>
                      </Space>
                      <Space>
                        <UserOutlined size={12} />
                        <Text size="small">{post.author}</Text>
                      </Space>
                    </Space>

                    {/* 文章标题 */}
                    <Link to={`/post/${post.id}`} className="post-title-link">
                      <Title level={4} className="post-title">
                        {post.title.length > 22 ? `${post.title.slice(0, 22)}...` : post.title}
                      </Title>
                    </Link>

                    {/* 文章标签 */}
                    <div className="post-tags">
                      {post.tags.map(tag => (
                        <Tag key={tag} size="small" color="blue">
                          {tag}
                        </Tag>
                      ))}
                    </div>

                    {/* 文章摘要 */}
                    <Paragraph
                      className="post-summary"
                      ellipsis={{ rows: 2, expandable: false, symbol: '...' }}
                    >
                      {post.summary || '暂无摘要'}
                    </Paragraph>

                    {/* 阅读按钮 */}
                    <Link to={`/post/${post.id}`} className="read-more-btn">
                      阅读全文 →
                    </Link>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card className="empty-card">
              <Text type="secondary" style={{ fontSize: '1rem' }}>
                暂无文章，快去 <Link to="/write">撰写文章</Link> 吧~
              </Text>
            </Card>
          )}
        </Col>

        {/* 右侧：热门推荐+标签云 */}
        <Col xs={24} lg={8}>
          {/* 热门推荐 */}
          <div className="side-section">
            <div className="section-header">
              <Title level={3}><FireOutlined /> 热门推荐</Title>
            </div>
            {hotPosts.length > 0 ? (
              <Card className="hot-posts-card">
                {hotPosts.map((post, index) => (
                  <Link to={`/post/${post.id}`} key={post.id} className="hot-post-item">
                    <div className="hot-post-rank">{index + 1}</div>
                    <div className="hot-post-content">
                      <Text className="hot-post-title">{post.title}</Text>
                      <Text type="secondary" size="small">{post.createTime}</Text>
                    </div>
                  </Link>
                ))}
              </Card>
            ) : (
              <Card className="empty-side-card">
                <Text type="secondary">暂无热门文章</Text>
              </Card>
            )}
          </div>

          {/* 标签云 */}
          <div className="side-section">
            <div className="section-header">
              <Title level={3}><TagOutlined /> 标签云</Title>
            </div>
            {allTags.length > 0 ? (
              <Card className="tag-cloud-card">
                <div className="tag-cloud">
                  {allTags.map(tag => (
                    <Tag key={tag} className="cloud-tag" color="random">
                      {tag}
                    </Tag>
                  ))}
                </div>
              </Card>
            ) : (
              <Card className="empty-side-card">
                <Text type="secondary">暂无标签</Text>
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Home;